import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  Typography,
  useTheme,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/AddOutlined";
import BookmarkIcon from "@mui/icons-material/BookmarkBorderOutlined";
import {
  NAV_TREE,
  navTarget,
  nodeUrl,
  nodeVisible,
  scopeReady,
} from "../../shared/navigationRegistry";
import { resolveViewedYearId } from "../../shared/schoolYear";
import { NavDrawerContext } from "../shared/NavDrawerButton";

// Mobile-only variant of SchoolNavMenu. Same NAV_TREE, same gating, same
// navigation behavior, but instead of expanding sub-links inline (accordion)
// it drills between two views: the top-level links, and a single parent's
// sub-links with a back arrow.
//
// The drill is a horizontal slide, modeled as a master/detail push/pop: both
// views live side by side on one track that translates, so there is never a
// blank frame between them.
//   - Drill in (forward): the track slides LEFT - the parent list exits to the
//     left while the sub-links enter from the right.
//   - Back (reverse): the track slides RIGHT - the sub-links exit to the right
//     while the parent list re-enters from the left.
// A per-view opacity fade rides along with the slide to soften the edges, and
// the viewport height animates so the dialog grows/shrinks smoothly between a
// short sub-list and the taller top-level list.

const SLIDE_MS = 300;
const EASE = "cubic-bezier(0.4, 0, 0.2, 1)";
// How far the viewport's clip region is pushed outward (px). The boxed items
// carry a soft glass drop-shadow (~18px blur); without this the viewport's clip
// would shear it off at every edge. `overflow: clip` + this margin keeps the
// off-screen pane hidden and the height-reveal clean while letting the shadow
// breathe past the box edges.
const SHADOW_PAD = 18;

// Trailing "+" that adds the row's destination to the dashboard bookmarks.
// Only rendered when onAddBookmark is supplied (Dashboard Menu only).
function AddBookmarkButton({ node, onAddBookmark, bookmarkedIds = [] }) {
  if (!onAddBookmark) return null;
  const added = bookmarkedIds.includes(node.id);
  return (
    <IconButton
      size="small"
      disabled={added}
      aria-label={
        added
          ? `${node.label} already bookmarked`
          : `Add ${node.label} bookmark`
      }
      onClick={(e) => {
        e.stopPropagation();
        onAddBookmark(node.id);
      }}
      sx={{ ml: 1 }}
    >
      {added ? (
        <BookmarkIcon sx={{ fontSize: 20, color: "#00000080" }} />
      ) : (
        <AddIcon sx={{ fontSize: 20, color: "primary.main" }} />
      )}
    </IconButton>
  );
}

// Fixed-width leading slot showing the drill-in affordance. It is a plain
// indicator now, not a button - the whole parent row opens the sub-view (groups
// carry no route, so tapping one only drills in). Leaves get an empty box the
// same width so every row's icon and label columns line up.
function ExpandSlot({ hasChildren }) {
  return (
    <Box
      sx={{
        width: 30,
        mr: 0.5,
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {hasChildren && (
        <ChevronRightIcon sx={{ fontSize: 22, color: "#00000080" }} />
      )}
    </Box>
  );
}

// `boxed` wraps each menu row in its own glass container. The Dashboard
// launchpad turns this on; the drawer/sidebar leaves it off and renders a plain
// list. The back-arrow row in the sub-view stays bare (it is a control, not a
// destination), matching how the Hide/Lock controls sit outside any box.
export default function SchoolNavMenuMobile({
  appProps,
  schoolSlug,
  schoolYearId = null,
  navigationMode = "spa",
  onAddBookmark = null,
  bookmarkedIds = [],
  boxed = false,
}) {
  const theme = useTheme();
  const spaNavigate = useNavigate();
  const location = useLocation();
  const { close: closeDrawer } = useContext(NavDrawerContext);

  // Glass surface applied to each row when boxed. Hoisted here so both views
  // share one definition. `overflow: hidden` clips the row's hover/active
  // highlight to the box's rounded corners.
  const boxSx = boxed ? { ...theme.custom.glassBox, overflow: "hidden" } : null;

  // Derive the year exactly like SchoolNavMenu so year-scoped links resolve
  // to a sensible default instead of staying greyed-out. Purely derived here -
  // the drawer has no year selector.
  const yearId = resolveViewedYearId(appProps, schoolYearId);

  const ctx = {
    schoolId: appProps?.school?.id ?? null,
    schoolSlug,
    school: appProps?.school ?? null,
    permissions: appProps?.permissions ?? {},
    yearId,
  };

  const nodes = useMemo(
    () => NAV_TREE.filter((node) => nodeVisible(node, ctx)),
    [ctx.schoolId, ctx.schoolSlug, ctx.yearId]
  );

  const currentPath =
    navigationMode === "browser" ? window.location.pathname : location.pathname;
  const isActive = (node) => nodeUrl(node, ctx)?.split("?")[0] === currentPath;

  // `activeId` drives the slide: null shows the top-level list (track at 0),
  // any id shows that parent's sub-links (track shifted one pane left).
  const [activeId, setActiveId] = useState(null);
  // The parent rendered in the sub pane. Kept set while sliding BACK so the
  // outgoing sub-links stay visible until they finish leaving; only ever
  // updated from the top-level view (where the sub pane is off-screen), so the
  // swap is never seen.
  const [subNode, setSubNode] = useState(null);

  // Blocks a new drill while one is mid-flight so a fast double-tap can't swap
  // the sub pane's content while it is still partway through a slide. Released
  // by a timer (a slide always lasts SLIDE_MS) rather than transitionend, so a
  // retargeted/interrupted transition can never leave it stuck.
  const animating = useRef(false);
  const lockTimer = useRef(null);
  const lock = () => {
    animating.current = true;
    clearTimeout(lockTimer.current);
    lockTimer.current = setTimeout(() => {
      animating.current = false;
    }, SLIDE_MS);
  };
  useEffect(() => () => clearTimeout(lockTimer.current), []);

  const openParent = (node) => {
    if (animating.current) return;
    lock();
    setSubNode(node);
    setActiveId(node.id);
  };
  const goBack = () => {
    if (animating.current) return;
    lock();
    setActiveId(null);
  };

  const handleNavigate = (node) => {
    const target = navTarget(node, ctx);
    if (!target || !scopeReady(target, ctx)) return;
    const url = target.resolve(ctx);
    if (navigationMode === "browser") {
      window.location.assign(url);
    } else if (!target.v1) {
      spaNavigate(url);
    } else {
      window.location.href = url;
    }
    closeDrawer?.();
  };

  // Animate the viewport to the height of whichever pane is showing, so the
  // dialog resizes smoothly instead of staying locked to the tallest view.
  // Measured before paint so the first frame already has a concrete height.
  const mainRef = useRef(null);
  const subRef = useRef(null);
  const [viewportHeight, setViewportHeight] = useState(undefined);
  useLayoutEffect(() => {
    const el = activeId ? subRef.current : mainRef.current;
    if (el) setViewportHeight(el.offsetHeight);
  }, [activeId, subNode, nodes]);

  const renderMainView = () => (
    <List
      sx={{
        p: 0,
        ...(boxed && { display: "flex", flexDirection: "column", gap: 1 }),
      }}
    >
      {nodes.map((node) => {
        const Icon = node.icon;
        const children = (node.children ?? []).filter((child) =>
          nodeVisible(child, ctx)
        );
        const hasChildren = children.length > 0;
        const ready = scopeReady(node, ctx);
        // Groups never highlight as the current page (they have no route);
        // only leaf rows do.
        const active = hasChildren ? false : isActive(node);

        return (
          <Box key={node.id} sx={boxSx ?? undefined}>
            <ListItemButton
              aria-current={active ? "page" : undefined}
              aria-disabled={!ready}
              onClick={() =>
                hasChildren ? openParent(node) : handleNavigate(node)
              }
              sx={{
                borderRadius: boxed ? 0 : 1,
                py: 1,
                pl: 0.5,
                opacity: ready ? 1 : 0.4,
                cursor: ready ? "pointer" : "not-allowed",
                backgroundColor: active ? "#E3F2FD" : "transparent",
                "&:hover": {
                  backgroundColor: active ? "#D6EAFB" : "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              {/* The whole row drills into the sub-view; this slot just shows
                  the chevron affordance. Leaves get an empty slot. */}
              <ExpandSlot hasChildren={hasChildren} />
              <ListItemIcon sx={{ minWidth: 38 }}>
                {Icon && <Icon sx={{ fontSize: 26, color: "#00000080" }} />}
              </ListItemIcon>
              <Typography
                sx={{
                  flex: 1,
                  fontSize: 18,
                  fontWeight: active ? 600 : 400,
                  color: "#000000",
                }}
              >
                {node.label}
              </Typography>
              <AddBookmarkButton
                node={node}
                onAddBookmark={onAddBookmark}
                bookmarkedIds={bookmarkedIds}
              />
            </ListItemButton>
          </Box>
        );
      })}
    </List>
  );

  const renderSubView = (node) => {
    const children = (node.children ?? []).filter((child) =>
      nodeVisible(child, ctx)
    );
    return (
      <>
        {/* The back row stays bare even when boxed - it is a control, like the
            Hide/Lock buttons, not a navigable destination. */}
        <ListItemButton
          aria-label="Back to menu"
          onClick={goBack}
          sx={{
            borderRadius: 1,
            py: 1,
            pl: 0.5,
            mb: boxed ? 1 : 0.5,
            "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
          }}
        >
          <ListItemIcon sx={{ minWidth: 38 }}>
            <ArrowBackIcon sx={{ fontSize: 24, color: "#00000080" }} />
          </ListItemIcon>
          <Typography
            sx={{ flex: 1, fontSize: 18, fontWeight: 600, color: "#000000" }}
          >
            {node.label}
          </Typography>
        </ListItemButton>
        <List
          sx={{
            p: 0,
            ...(boxed && { display: "flex", flexDirection: "column", gap: 1 }),
          }}
        >
          {children.map((child) => {
            const childReady = scopeReady(child, ctx);
            const childActive = isActive(child);
            return (
              <Box key={child.id} sx={boxSx ?? undefined}>
                <ListItemButton
                  aria-current={childActive ? "page" : undefined}
                  aria-disabled={!childReady}
                  onClick={() => handleNavigate(child)}
                  sx={{
                    borderRadius: boxed ? 0 : 1,
                    py: 1,
                    pl: 0.5,
                    opacity: childReady ? 1 : 0.4,
                    cursor: childReady ? "pointer" : "not-allowed",
                    backgroundColor: childActive ? "#E3F2FD" : "transparent",
                    "&:hover": {
                      backgroundColor: childActive
                        ? "#D6EAFB"
                        : "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  {/* Empty icon slot so child labels align under the back-button
                      label above them. */}
                  <ListItemIcon sx={{ minWidth: 38 }} />
                  <Typography
                    sx={{
                      flex: 1,
                      fontSize: 18,
                      fontWeight: childActive ? 600 : 400,
                      color: "#000000",
                    }}
                  >
                    {child.label}
                  </Typography>
                  <AddBookmarkButton
                    node={child}
                    onAddBookmark={onAddBookmark}
                    bookmarkedIds={bookmarkedIds}
                  />
                </ListItemButton>
              </Box>
            );
          })}
        </List>
      </>
    );
  };

  const showingSub = Boolean(activeId);

  return (
    <Box sx={{ pb: 3 }}>
      {/* Viewport: clips the off-screen pane and animates its height to the
          pane in view. `overflow: clip` + clip-margin clips the sliding panes
          (and reveals the height smoothly) without shearing each item's glass
          shadow, which `overflow: hidden` would cut off at the edges. */}
      <Box
        sx={{
          position: "relative",
          overflow: "clip",
          overflowClipMargin: `${SHADOW_PAD}px`,
          height: viewportHeight,
          transition: `height ${SLIDE_MS}ms ${EASE}`,
        }}
      >
        {/* Track: two panes side by side, shifted one pane left to reveal the
            sub-links. */}
        <Box
          sx={{
            display: "flex",
            // Panes size to their own content (not stretched to the taller
            // sibling) so each pane's measured height is its real height.
            alignItems: "flex-start",
            width: "200%",
            transform: showingSub ? "translateX(-50%)" : "translateX(0)",
            transition: `transform ${SLIDE_MS}ms ${EASE}`,
          }}
        >
          <Box
            ref={mainRef}
            aria-hidden={showingSub ? true : undefined}
            inert={showingSub ? true : undefined}
            sx={{
              width: "50%",
              flexShrink: 0,
              opacity: showingSub ? 0 : 1,
              transition: `opacity ${SLIDE_MS}ms ${EASE}`,
            }}
          >
            {renderMainView()}
          </Box>
          <Box
            ref={subRef}
            aria-hidden={showingSub ? undefined : true}
            inert={showingSub ? undefined : true}
            sx={{
              width: "50%",
              flexShrink: 0,
              opacity: showingSub ? 1 : 0,
              transition: `opacity ${SLIDE_MS}ms ${EASE}`,
            }}
          >
            {subNode && renderSubView(subNode)}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
